/**
 * BH/FH飞行小时统计Widget（混合数据源）
 * 展示结算数据与MVT实时数据混合的飞行小时统计
 * 按飞机分组展示，每架飞机一行图表
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Spin, Empty, Table, Typography } from "antd";
import * as echarts from "echarts";
import dayjs from "dayjs";
import WidgetWrapper from "../../components/WidgetWrapper";
import { flightOperationStatsService } from "../../../../api/enhancedApi";

const { Text } = Typography;


/**
 * BH/FH图表组件（使用色调区分数据源，响应式）
 */
const FlightHoursChart = ({ aircraftReg, data, height = 180 }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current || !data || data.length === 0) return;

        // 初始化图表实例
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        // 获取容器宽度，判断是否显示标签
        const containerWidth = containerRef.current?.offsetWidth || 800;
        const dataPoints = data.length;
        const avgWidthPerPoint = containerWidth / dataPoints;
        const showLabels = avgWidthPerPoint > 20; // 每个数据点宽度>20px时显示标签

        // 格式化日期显示
        const formatDate = (dateStr) => {
            const date = new Date(dateStr);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        };

        const dates = data.map(item => formatDate(item.flight_date));
        
        // 根据数据源设置颜色（色调区分）
        const getBhColor = (source) => {
            switch (source) {
                case 'SETTLEMENT': return '#0050b3';  // 深蓝
                case 'MIXED': return '#1890ff';       // 标准蓝
                case 'MVT': return '#69c0ff';         // 浅蓝
                default: return '#69c0ff';
            }
        };

        const getFhColor = (source) => {
            switch (source) {
                case 'SETTLEMENT': return '#fa541c';  // 深橙
                case 'MIXED': return '#ff7a45';       // 标准橙
                case 'MVT': return '#ffbb96';         // 浅橙
                default: return '#ffbb96';
            }
        };

        // 准备BH和FH系列数据，每个数据点使用不同色调
        const bhData = data.map(item => ({
            value: item.bh,
            itemStyle: {
                color: getBhColor(item.source)
            }
        }));

        const fhData = data.map(item => ({
            value: item.fh,
            itemStyle: {
                color: getFhColor(item.source)
            }
        }));

        // 配置图表选项
        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { 
                    type: 'shadow',
                    shadowStyle: {
                        color: 'rgba(0, 0, 0, 0.05)'
                    }
                },
                backgroundColor: '#fff',
                borderColor: '#d9d9d9',
                borderWidth: 1,
                padding: 10,
                textStyle: {
                    color: '#595959',
                    fontSize: 11,
                    fontFamily: 'Microsoft YaHei, sans-serif'
                },
                extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); border-radius: 4px;',
                formatter: (params) => {
                    const index = params[0].dataIndex;
                    const item = data[index];
                    const sourceText = item.source === 'SETTLEMENT' ? '结算' : 
                                      item.source === 'MIXED' ? '混合' : 'MVT';
                    const bhColor = getBhColor(item.source);
                    const fhColor = getFhColor(item.source);
                    return `<div style="line-height: 1.8;">
                                <strong style="color: #262626;">${aircraftReg}</strong> 
                                <span style="color: #8c8c8c; font-size: 10px;">${item.flight_date}</span><br/>
                                <span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background-color:${bhColor};"></span>
                                BH: <strong style="color: #262626;">${item.bh ? item.bh.toFixed(1) : '0.0'}</strong>h<br/>
                                <span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background-color:${fhColor};"></span>
                                FH: <strong style="color: #262626;">${item.fh ? item.fh.toFixed(1) : '0.0'}</strong>h<br/>
                                <span style="color: #8c8c8c; font-size: 10px;">来源: ${sourceText}</span>
                            </div>`;
                }
            },
            grid: {
                left: '8%',
                right: '4%',
                bottom: '18%',
                top: showLabels ? '15%' : '8%',
                containLabel: false
            },
            xAxis: {
                type: 'category',
                data: dates,
                    axisLabel: {
                        rotate: 45,
                        fontSize: 10,
                        color: '#8c8c8c',
                        fontFamily: 'Microsoft YaHei, sans-serif',
                        margin: 8,
                        interval: 0  // 显示所有日期
                    },
                axisLine: { 
                    lineStyle: { 
                        color: '#e8e8e8',
                        width: 1
                    } 
                },
                axisTick: { 
                    show: true,
                    lineStyle: {
                        color: '#e8e8e8'
                    }
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    fontSize: 10,
                    color: '#8c8c8c',
                    fontFamily: 'Microsoft YaHei, sans-serif',
                    formatter: '{value}h'
                },
                splitLine: { 
                    lineStyle: { 
                        color: '#f0f0f0',
                        type: 'dashed'
                    } 
                },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            series: [
                {
                    name: 'BH',
                    type: 'bar',
                    data: bhData,
                    barGap: '20%',
                    barWidth: '35%',
                    itemStyle: {
                        borderRadius: [2, 2, 0, 0]
                    },
                    label: {
                        show: showLabels,
                        position: 'top',
                        formatter: (params) => params.value?.toFixed(1) || '0.0',
                        fontSize: 9,
                        fontWeight: 600,
                        fontFamily: 'Microsoft YaHei, sans-serif',
                        color: '#595959'
                    }
                },
                {
                    name: 'FH',
                    type: 'bar',
                    data: fhData,
                    barWidth: '35%',
                    itemStyle: {
                        borderRadius: [2, 2, 0, 0]
                    },
                    label: {
                        show: showLabels,
                        position: 'top',
                        formatter: (params) => params.value?.toFixed(1) || '0.0',
                        fontSize: 9,
                        fontWeight: 600,
                        fontFamily: 'Microsoft YaHei, sans-serif',
                        color: '#595959'
                    }
                }
            ]
        };

        chartInstance.current.setOption(option, true); // 第二个参数true表示不合并

        // 响应式调整 - 使用 requestAnimationFrame 防止 ResizeObserver 错误
        let resizeTimer = null;
        const handleResize = () => {
            if (resizeTimer) {
                cancelAnimationFrame(resizeTimer);
            }
            resizeTimer = requestAnimationFrame(() => {
                if (chartInstance.current && containerRef.current) {
                    try {
                        chartInstance.current.resize();
                        // 重新计算是否显示标签
                        const newContainerWidth = containerRef.current.offsetWidth || 800;
                        const newAvgWidthPerPoint = newContainerWidth / dataPoints;
                        const newShowLabels = newAvgWidthPerPoint > 20;
                        
                        if (newShowLabels !== showLabels) {
                            // 如果标签显示状态改变，重新设置配置
                            const newOption = {
                                series: [
                                    { label: { show: newShowLabels } },
                                    { label: { show: newShowLabels } }
                                ]
                            };
                            chartInstance.current.setOption(newOption);
                        }
                    } catch (error) {
                        // 忽略 ResizeObserver 错误
                        if (error.message !== 'ResizeObserver loop completed with undelivered notifications.') {
                            console.error('Chart resize error:', error);
                        }
                    }
                }
            });
        };
        
        window.addEventListener('resize', handleResize);
        
        // 使用 ResizeObserver 监听容器大小变化（拖拽调整Widget大小时）
        let resizeObserver = null;
        try {
            resizeObserver = new ResizeObserver((entries) => {
                // 使用 requestAnimationFrame 防止循环错误
                window.requestAnimationFrame(() => {
                    if (!Array.isArray(entries) || !entries.length) {
                        return;
                    }
                    handleResize();
                });
            });
            
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
        } catch (error) {
            console.warn('ResizeObserver not supported:', error);
        }

        return () => {
            if (resizeTimer) {
                cancelAnimationFrame(resizeTimer);
            }
            window.removeEventListener('resize', handleResize);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [aircraftReg, data]);

    // 组件卸载时销毁图表
    useEffect(() => {
        return () => {
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    if (!data || data.length === 0) {
        return (
            <div 
                ref={containerRef}
                style={{ 
                    height: `${height}px`, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#8c8c8c',
                    fontSize: '12px'
                }}>
                暂无数据
            </div>
        );
    }

    return (
        <div 
            ref={containerRef}
            style={{ 
                width: '100%', 
                height: `${height}px`,
                position: 'relative'
            }}>
            <div 
                ref={chartRef} 
                style={{ 
                    width: '100%', 
                    height: '100%',
                    background: 'white',
                    borderRadius: '4px'
                }} 
            />
        </div>
    );
};

/**
 * 图例组件（固定当月，不提供月份筛选），展示当前月份日期范围
 */
const ChartLegend = ({ dateRange }) => {
    const rangeText = dateRange && dateRange.length === 2
        ? `${dateRange[0].format('YYYY年MM月')} (${dateRange[0].format('YYYY-MM-DD')} ~ ${dateRange[1].format('YYYY-MM-DD')})`
        : '';
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
            padding: '8px 12px',
            marginBottom: '12px',
            background: '#fafafa',
            borderRadius: '6px',
            border: '1px solid #e8e8e8'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                        width: '16px',
                        height: '10px',
                        background: '#1890ff',
                        borderRadius: '2px'
                    }} />
                    <span style={{
                        fontSize: '12px',
                        color: '#595959',
                        fontFamily: 'Microsoft YaHei, sans-serif'
                    }}>BH (Block Hours)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                        width: '16px',
                        height: '10px',
                        background: '#ff7a45',
                        borderRadius: '2px'
                    }} />
                    <span style={{
                        fontSize: '12px',
                        color: '#595959',
                        fontFamily: 'Microsoft YaHei, sans-serif'
                    }}>FH (Flight Hours)</span>
                </div>
            </div>
            {rangeText && (
                <span style={{
                    fontSize: '12px',
                    color: '#8c8c8c',
                    fontFamily: 'Microsoft YaHei, sans-serif'
                }}>
                    统计月份：{rangeText}
                </span>
            )}
        </div>
    );
};

/**
 * 飞机汇总表格组件
 * @param {Object} monthlyData - 当月数据（按飞机分组）
 * @param {Object} yearlyData - 年度数据（按飞机分组）
 * @param {Array} currentMonthRange - 当月日期范围 [start, end]
 */
const AircraftSummaryTable = ({ monthlyData, yearlyData, aggregatedMetrics, currentMonthRange }) => {
    // 计算汇总数据
    const summaryData = useMemo(() => {
        // 获取所有飞机（合并当月、年度和聚合指标的飞机列表）
        const allAircraft = new Set([
            ...Object.keys(monthlyData || {}),
            ...Object.keys(yearlyData || {}),
            ...Object.keys(aggregatedMetrics || {})
        ]);
        
        return Array.from(allAircraft).map(reg => {
            const metrics = aggregatedMetrics[reg];
            
            // 如果有新的聚合指标数据，使用它；否则降级使用前端计算
            if (metrics && metrics.monthly && metrics.yearly) {
                return {
                    key: reg,
                    aircraftReg: reg,
                    // 当月指标（使用后端聚合数据）
                    bhDailyAvg: metrics.monthly.bh_daily_avg.toFixed(2),
                    monthTotalBh: metrics.monthly.total_bh.toFixed(2),
                    monthTotalFh: metrics.monthly.total_fh.toFixed(2),
                    monthTotalSegments: metrics.monthly.total_cycles,
                    monthFhPerCycle: metrics.monthly.fh_per_cycle != null ? metrics.monthly.fh_per_cycle.toFixed(2) : '—',
                    // 年度指标（使用后端聚合数据）
                    yearTotalBh: metrics.yearly.total_bh.toFixed(2),
                    yearTotalFh: metrics.yearly.total_fh.toFixed(2),
                    yearTotalSegments: metrics.yearly.total_cycles,
                    yearFhPerCycle: metrics.yearly.fh_per_cycle != null ? metrics.yearly.fh_per_cycle.toFixed(2) : '—',
                    yearMonthlyAvgBh: metrics.yearly.monthly_avg_bh.toFixed(2)
                };
            }
            
            // 降级：使用前端自行聚合（保留原有逻辑作为兜底）
            const now = dayjs();
            const currentYear = now.year();
            const currentMonth = now.month();
            const completedMonths = currentMonth;
            const daysInMonth = currentMonthRange && currentMonthRange.length === 2 
                ? currentMonthRange[1].diff(currentMonthRange[0], 'day') + 1
                : 30;
            
            const monthData = monthlyData[reg] || [];
            const monthTotalCycles = monthData.reduce((sum, item) => sum + (item.leg_count ?? item.cycle ?? 0), 0);
            const monthTotalBh = monthData.reduce((sum, item) => sum + (item.bh || 0), 0);
            const monthTotalFh = monthData.reduce((sum, item) => sum + (item.fh || 0), 0);
            const bhDailyAvg = daysInMonth > 0 ? monthTotalBh / daysInMonth : 0;
            const monthFhPerCycle = monthTotalCycles > 0 ? monthTotalFh / monthTotalCycles : null;

            const yearData = yearlyData[reg] || [];
            const yearTotalCycles = yearData.reduce((sum, item) => sum + (item.leg_count ?? item.cycle ?? 0), 0);
            const yearTotalBh = yearData.reduce((sum, item) => sum + (item.bh || 0), 0);
            const yearTotalFh = yearData.reduce((sum, item) => sum + (item.fh || 0), 0);
            const yearFhPerCycle = yearTotalCycles > 0 ? yearTotalFh / yearTotalCycles : null;

            const completedMonthsData = yearData.filter(item => {
                const itemDate = dayjs(item.flight_date);
                return itemDate.year() === currentYear && itemDate.month() < currentMonth;
            });
            const completedMonthsBh = completedMonthsData.reduce((sum, item) => sum + (item.bh || 0), 0);
            const yearMonthlyAvgBh = completedMonths > 0 ? completedMonthsBh / completedMonths : 0;

            return {
                key: reg,
                aircraftReg: reg,
                bhDailyAvg: bhDailyAvg.toFixed(2),
                monthTotalBh: monthTotalBh.toFixed(2),
                monthTotalFh: monthTotalFh.toFixed(2),
                monthTotalSegments: monthTotalCycles,
                monthFhPerCycle: monthFhPerCycle != null ? monthFhPerCycle.toFixed(2) : '—',
                yearTotalBh: yearTotalBh.toFixed(2),
                yearTotalFh: yearTotalFh.toFixed(2),
                yearTotalSegments: yearTotalCycles,
                yearFhPerCycle: yearFhPerCycle != null ? yearFhPerCycle.toFixed(2) : '—',
                yearMonthlyAvgBh: yearMonthlyAvgBh.toFixed(2)
            };
        }).sort((a, b) => a.aircraftReg.localeCompare(b.aircraftReg));
    }, [monthlyData, yearlyData, aggregatedMetrics, currentMonthRange]);

    const columns = [
        {
            title: '飞机注册号',
            dataIndex: 'aircraftReg',
            key: 'aircraftReg',
            width: 120,
            fixed: 'left',
            align: 'center',
            render: (text) => (
                <Text strong style={{ fontSize: 13, color: '#1890ff' }}>
                    {text}
                </Text>
            )
        },
        {
            title: 'BH日均 (h)',
            dataIndex: 'bhDailyAvg',
            key: 'bhDailyAvg',
            width: 100,
            align: 'right',
            render: (text) => (
                <Text style={{ color: '#52c41a', fontSize: 13 }}>
                    {text}
                </Text>
            )
        },
        {
            title: '月总BH (h)',
            dataIndex: 'monthTotalBh',
            key: 'monthTotalBh',
            width: 100,
            align: 'right',
            render: (text) => (
                <Text strong style={{ color: '#1890ff', fontSize: 13 }}>
                    {text}
                </Text>
            )
        },
        {
            title: '月总FH (h)',
            dataIndex: 'monthTotalFh',
            key: 'monthTotalFh',
            width: 100,
            align: 'right',
            render: (text) => (
                <Text strong style={{ color: '#ff7a45', fontSize: 13 }}>
                    {text}
                </Text>
            )
        },
        {
            title: '总航段数',
            dataIndex: 'monthTotalSegments',
            key: 'monthTotalSegments',
            width: 90,
            align: 'center',
            render: (text) => (
                <Text style={{ color: '#8c8c8c', fontSize: 13 }}>
                    {text} 段
                </Text>
            )
        },
        {
            title: '月度小时循环比',
            dataIndex: 'monthFhPerCycle',
            key: 'monthFhPerCycle',
            width: 110,
            align: 'right',
            render: (text) => (
                <Text style={{ color: '#ff7a45', fontSize: 13 }}>
                    {text}
                </Text>
            )
        },
        {
            title: '年度总BH (h)',
            dataIndex: 'yearTotalBh',
            key: 'yearTotalBh',
            width: 110,
            align: 'right',
            render: (text) => (
                <Text strong style={{ color: '#722ed1', fontSize: 13 }}>
                    {text}
                </Text>
            )
        },
        {
            title: '年度总FH (h)',
            dataIndex: 'yearTotalFh',
            key: 'yearTotalFh',
            width: 110,
            align: 'right',
            render: (text) => (
                <Text strong style={{ color: '#eb2f96', fontSize: 13 }}>
                    {text}
                </Text>
            )
        },
        {
            title: '年度小时循环比',
            dataIndex: 'yearFhPerCycle',
            key: 'yearFhPerCycle',
            width: 110,
            align: 'right',
            render: (text) => (
                <Text style={{ color: '#eb2f96', fontSize: 13 }}>
                    {text}
                </Text>
            )
        },
        {
            title: '年度月均BH (h)',
            dataIndex: 'yearMonthlyAvgBh',
            key: 'yearMonthlyAvgBh',
            width: 120,
            align: 'right',
            render: (text) => (
                <Text style={{ color: '#13c2c2', fontSize: 13 }}>
                    {text}
                </Text>
            )
        }
    ];

    return (
        <div style={{ 
            marginTop: '20px',
            borderRadius: '4px',
            overflow: 'hidden'
        }}>
            <Table
                columns={columns}
                dataSource={summaryData}
                pagination={false}
                size="small"
                bordered
                rowKey="aircraftReg"
                scroll={{ x: 'max-content' }}
                style={{
                    background: '#fff',
                    borderRadius: '4px'
                }}
            />
        </div>
    );
};

/**
 * 飞机图表行组件
 */
const AircraftChartRow = ({ aircraftReg, data, loading = false }) => {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px',
            padding: '12px',
            background: '#ffffff',
            borderRadius: '6px',
            border: '1px solid #e8e8e8',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
            e.currentTarget.style.borderColor = '#d9d9d9';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.03)';
            e.currentTarget.style.borderColor = '#e8e8e8';
        }}>
            {/* 飞机号标签 */}
            <div style={{
                minWidth: '90px',
                maxWidth: '90px',
                fontSize: '14px',
                fontWeight: 700,
                color: '#1890ff',
                textAlign: 'center',
                padding: '10px 8px',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)',
                borderRadius: '6px',
                border: '1px solid #91d5ff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: 'Microsoft YaHei, sans-serif',
                letterSpacing: '0.5px'
            }}>
                {aircraftReg}
            </div>

            {/* 图表容器 */}
            <div style={{ flex: 1 }}>
                {loading ? (
                    <div style={{ 
                        height: '180px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#8c8c8c',
                        fontSize: '12px'
                    }}>
                        加载中...
                    </div>
                ) : (
                    <FlightHoursChart 
                        aircraftReg={aircraftReg} 
                        data={data} 
                        height={180}
                    />
                )}
            </div>
        </div>
    );
};

/**
 * 主Widget组件
 */
const FlightHoursMixedWidget = () => {
    const [loading, setLoading] = useState(false);
    const [monthlyData, setMonthlyData] = useState({});  // 当月数据（用于图表）
    const [yearlyData, setYearlyData] = useState({});    // 年度数据（用于图表）
    const [aggregatedMetrics, setAggregatedMetrics] = useState({});  // 聚合指标数据（用于表格）
    
    // 固定当前月，不提供筛选
    const currentMonth = dayjs();
    
    // 根据月份获取日期范围
    const getMonthRange = (month) => {
        const start = month.startOf('month');  // 月初1日
        const end = month.endOf('month');      // 月末最后一天
        return [start, end];
    };
    
    // 获取年度日期范围：从1月1日到今天（固定）
    const getYearRange = () => {
        const now = dayjs();
        const start = now.startOf('year');   // 今年1月1日
        const end = now;                      // 今天
        return [start, end];
    };
    
    // 当月日期范围（固定）
    const dateRange = getMonthRange(currentMonth);

    // 辅助函数：按飞机分组数据
    const groupByAircraft = (items) => {
        const grouped = {};
        items.forEach(item => {
            if (!grouped[item.aircraft_reg]) {
                grouped[item.aircraft_reg] = [];
            }
            grouped[item.aircraft_reg].push(item);
        });
        // 按日期排序
        Object.keys(grouped).forEach(reg => {
            grouped[reg].sort((a, b) => 
                new Date(a.flight_date) - new Date(b.flight_date)
            );
        });
        return grouped;
    };

    // 兼容多种响应结构（items 可能在 response.items 或 response.data.items）
    const getItemsFromResponse = (response) => response?.items ?? response?.data?.items ?? [];

    // 获取月度数据
    const fetchMonthlyData = async (month) => {
        const [monthStart, monthEnd] = getMonthRange(month);
        const response = await flightOperationStatsService.getFlightHoursTimeSeries({
            start_date: monthStart.format('YYYY-MM-DD'),
            end_date: monthEnd.format('YYYY-MM-DD')
        });
        return groupByAircraft(getItemsFromResponse(response));
    };

    // 获取年度数据（只在首次加载时获取）
    const fetchYearlyData = async () => {
        const [yearStart, yearEnd] = getYearRange();
        const response = await flightOperationStatsService.getFlightHoursTimeSeries({
            start_date: yearStart.format('YYYY-MM-DD'),
            end_date: yearEnd.format('YYYY-MM-DD')
        });
        return groupByAircraft(getItemsFromResponse(response));
    };

    // 获取聚合指标数据（所有飞机）
    const fetchAggregatedMetrics = async () => {
        const now = dayjs();
        const year = now.year();
        const month = now.month() + 1; // dayjs月份是0-indexed
        
        // 获取所有飞机的时间序列数据，提取飞机注册号列表
        const [monthData, yearData] = await Promise.all([
            fetchMonthlyData(currentMonth),
            fetchYearlyData()
        ]);
        
        // 提取所有飞机注册号
        const aircraftRegs = new Set([...Object.keys(monthData), ...Object.keys(yearData)]);
        
        // 并行获取所有飞机的聚合指标
        const metricsPromises = Array.from(aircraftRegs).map(async (reg) => {
            try {
                const response = await flightOperationStatsService.getAggregatedMetrics({
                    aircraft_reg: reg,
                    year: year,
                    month: month
                });
                return { reg, data: response?.data || response };
            } catch (error) {
                console.error(`获取${reg}聚合指标失败:`, error);
                return { reg, data: null };
            }
        });
        
        const metricsResults = await Promise.all(metricsPromises);
        
        // 转换为对象格式
        const metrics = {};
        metricsResults.forEach(({ reg, data }) => {
            if (data) {
                metrics[reg] = data;
            }
        });
        
        return { monthData, yearData, metrics };
    };

    // 初始化加载（固定当月 + 年度 + 聚合指标）
    const fetchData = async () => {
        setLoading(true);
        try {
            const { monthData, yearData, metrics } = await fetchAggregatedMetrics();

            setMonthlyData(monthData);
            setYearlyData(yearData);
            setAggregatedMetrics(metrics);
        } catch (error) {
            console.error("获取飞行小时数据失败:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const aircraftRegs = Object.keys(monthlyData);

    return (
        <WidgetWrapper 
            title="BH/FH飞行小时统计" 
            loading={loading} 
            onRefresh={fetchData}
        >
            <div style={{ 
                height: "100%", 
                minHeight: "250px",
                overflow: "auto",
                padding: "8px"
            }}>
                {aircraftRegs.length > 0 ? (
                    <div>
                        {/* 图例与当前月份日期范围 */}
                        <ChartLegend dateRange={dateRange} />
                        
                        {/* 汇总表格 */}
                        <AircraftSummaryTable 
                            monthlyData={monthlyData}
                            yearlyData={yearlyData}
                            aggregatedMetrics={aggregatedMetrics}
                            currentMonthRange={dateRange}
                        />
                        
                        {/* 时间序列图表 */}
                        <div style={{ marginTop: '20px' }}>
                            {aircraftRegs.map(reg => (
                                <AircraftChartRow
                                    key={reg}
                                    aircraftReg={reg}
                                    data={monthlyData[reg]}
                                    loading={false}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <Empty description="暂无飞行小时数据" />
                )}
            </div>
        </WidgetWrapper>
    );
};

export default FlightHoursMixedWidget;
